/**
 * How to present the things we can monitor, and what each one actually is.
 *
 * The picker used to be a flat 3-column grid in whatever order the API returned, with the one-line
 * Description from the provider document as its only explanation. That put "Gemini", "Google AI
 * Overview" and "Google AI Mode" in three different places on the grid with nothing to distinguish
 * them, and they are the three most easily confused entries in the product: same vendor, same
 * underlying model family, three completely different places a buyer meets an answer.
 *
 * Keyed by LlmProvider.Name as seeded in Backend/BrandKarma.Web/Data/LlmProviders.cs. A provider
 * with no entry here still renders — it just falls back to its own Description and the last group.
 */

/** Group order top to bottom. Google first, because it is where the confusion is. */
export const PROVIDER_GROUPS = [
  {
    key: "google",
    label: "Google",
    /** Three entries, so at cols=3 they occupy exactly one row — what makes them comparable. */
    hint:
      "Three separate places a Google user can meet an answer about you. They draw on the same " +
      "models but retrieve differently, so a brand can be strong in one and absent from another.",
  },
  {
    key: "assistants",
    label: "AI assistants",
    hint: "Chat assistants answering from their own knowledge, plus web search where they use it.",
  },
];

/**
 * `blurb` replaces the server Description on the card; `tooltip` is the longer explanation.
 *
 * The tooltips answer one question: what would have to be true for this surface to mention you.
 * That is the only thing that makes the three Google entries worth having as three checkboxes
 * rather than one, and it is what decides which of them is worth paying for.
 */
export const PROVIDER_GUIDE = {
  Gemini: {
    group: "google",
    blurb: "The Gemini assistant",
    tooltip:
      "Google's chat assistant at gemini.google.com. Answers mostly from what the model learnt in " +
      "training, so being mentioned here reflects how well known you are, not how well you rank. " +
      "Slowest to reflect a change on your site.",
  },
  "Google AI Overview": {
    group: "google",
    blurb: "The AI summary above normal search results",
    tooltip:
      "The AI paragraph Google puts above the blue links for an ordinary search. Built from pages " +
      "it has indexed, so it follows your SEO closely — this is the surface that moves when your " +
      "content does. It is also the one with the most reach, because the user never asked for AI.",
  },
  "Google AI Mode": {
    group: "google",
    blurb: "Google search in conversational mode",
    tooltip:
      "The separate 'AI Mode' tab, where Google answers a question conversationally instead of " +
      "listing results. It runs several searches behind one question, so it reaches further down " +
      "than AI Overview does and can surface pages that never rank on page one.",
  },
  OpenAI: {
    group: "assistants",
    blurb: "ChatGPT's models",
    tooltip:
      "The models behind ChatGPT. The largest assistant audience by a wide margin, so for most " +
      "brands this is the single most consequential answer to get right.",
  },
  Claude: {
    group: "assistants",
    blurb: "Anthropic's Claude",
    tooltip:
      "Anthropic's assistant. Smaller reach than ChatGPT, but skewed towards technical and " +
      "professional users, which matters more than raw volume for a B2B brand.",
  },
  Grok: {
    group: "assistants",
    blurb: "xAI's Grok",
    tooltip:
      "xAI's assistant, built into X. Leans on very recent posts, so it reacts to news and " +
      "discussion faster than the others — and to a bad week just as fast.",
  },
  Perplexity: {
    group: "assistants",
    blurb: "Perplexity's answer engine",
    tooltip:
      "An answer engine that searches first and cites as it writes. Closest of the assistants to " +
      "classic search, so results here track your rankings more than your reputation.",
  },
};

/**
 * Groups the API's provider list for display, preserving each group's declared order.
 *
 * Anything unknown lands in the last group rather than being dropped — a provider seeded on the
 * server that this build has not heard of must still be checkable, or a customer cannot turn on
 * something they are being offered.
 */
export function groupProviders(providers) {
  const groups = PROVIDER_GROUPS.map((group) => ({ ...group, providers: [] }));
  const last = groups[groups.length - 1];

  for (const provider of providers ?? []) {
    const key = PROVIDER_GUIDE[provider.name]?.group;
    const target = groups.find((g) => g.key === key) ?? last;
    target.providers.push(provider);
  }

  // Within Google, keep the guide's own order (assistant, AI Overview, AI Mode) rather than the
  // insertion order of the provider documents, so the row reads from least to most search-driven.
  const guideOrder = Object.keys(PROVIDER_GUIDE);
  for (const group of groups) {
    group.providers.sort((a, b) => {
      const ai = guideOrder.indexOf(a.name);
      const bi = guideOrder.indexOf(b.name);
      return (ai < 0 ? Number.MAX_SAFE_INTEGER : ai) - (bi < 0 ? Number.MAX_SAFE_INTEGER : bi);
    });
  }

  return groups.filter((group) => group.providers.length > 0);
}

/** The guide entry for a provider, or a fallback built from its own server-side description. */
export function providerGuide(provider) {
  return (
    PROVIDER_GUIDE[provider?.name] ?? {
      group: "assistants",
      blurb: provider?.description ?? "",
      tooltip: provider?.description ?? "",
    }
  );
}
